// Manage SKU Categories — rename, edit default markup, drag-to-reorder,
// and delete (only when no SKUs reference the category — backend
// enforces FK_RESTRICT).
//
// Reorder uses @dnd-kit/sortable (pointer-based) — native HTML5 DnD is broken
// in the office Edge. The grip is the drag handle so the row's inline edit
// controls stay clickable.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Pencil,
  Plus,
  Trash2,
  Check as CheckIcon,
  X as XIcon,
} from 'lucide-react';
import type { SkuCategory, SkuCategoryCreateInput } from '@gutterinv/shared';
import {
  getSkuCategories,
  postSkuCategory,
  patchSkuCategory,
  deleteSkuCategory,
  reorderSkuCategories,
} from '../api/client.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';
import { Badge } from './ui/badge.js';
import { cn } from '../lib/utils.js';
import {
  categoryMatchesProductLine,
  isInsulationCategory,
} from '../lib/product-line.js';

export function CategoryManagerDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { data: categories, isLoading } = useQuery({
    queryKey: ['sku-categories'],
    queryFn: getSkuCategories,
  });

  const sorted = useMemo(
    () =>
      [...(categories ?? [])].sort(
        (a, b) =>
          a.displayOrder - b.displayOrder || a.name.localeCompare(b.name),
      ),
    [categories],
  );

  // Local order — lets the user drag without firing a network call per
  // hover; a single reorderMutation fires on drop.
  const [localOrder, setLocalOrder] = useState<string[]>(() =>
    sorted.map((c) => c.id),
  );
  useEffect(() => {
    setLocalOrder(sorted.map((c) => c.id));
  }, [sorted]);

  const orderedCategories = useMemo(() => {
    const map = new Map(sorted.map((c) => [c.id, c]));
    return localOrder.flatMap((id) => {
      const c = map.get(id);
      return c ? [c] : [];
    });
  }, [localOrder, sorted]);

  // Split by product line into two columns. Filters are exact complements, so
  // every category lands in exactly one column — the reorder payload below
  // concatenates them and must not drop any id.
  const orderedGutter = useMemo(
    () => orderedCategories.filter((c) => categoryMatchesProductLine(c.name, 'gutter')),
    [orderedCategories],
  );
  const orderedInsulation = useMemo(
    () => orderedCategories.filter((c) => isInsulationCategory(c.name)),
    [orderedCategories],
  );
  const gutterIds = useMemo(() => orderedGutter.map((c) => c.id), [orderedGutter]);
  const insulationIds = useMemo(
    () => orderedInsulation.map((c) => c.id),
    [orderedInsulation],
  );

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderSkuCategories(ids),
    onSuccess: () => {
      // Categories AND the inventory list must both refetch — the SKU list is
      // grouped by category in the SQL ORDER BY.
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // One handler per column. Reorder happens WITHIN a column's id list, then the
  // full order is rebuilt as [gutter, insulation] and sent whole — the backend
  // rewrites display_order = 0..N by index, so sending a partial list (or one
  // column starting at 0) would mis-order the lines relative to each other.
  const handleColumnDragEnd =
    (line: 'gutter' | 'insulation') => (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const colIds = line === 'gutter' ? gutterIds : insulationIds;
      const oldIndex = colIds.indexOf(active.id as string);
      const newIndex = colIds.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const newColIds = arrayMove(colIds, oldIndex, newIndex);
      const full =
        line === 'gutter'
          ? [...newColIds, ...insulationIds]
          : [...gutterIds, ...newColIds];
      setLocalOrder(full);
      reorderMutation.mutate(full);
    };

  const [creating, setCreating] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cap height + template the rows so the title/footer pin and ONLY the category
          list scrolls — the content (2 columns of categories) can exceed the viewport.
          Keep the primitive's `grid`; just give it 3 rows [header · scroll · footer]
          (minmax(0,1fr) lets the middle row shrink so its overflow-y-auto engages). */}
      <DialogContent
        className="sm:max-w-[67rem] max-h-[85vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto">
        {isLoading ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="space-y-3 py-2">
            {orderedCategories.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-secondary/30 px-4 py-8 text-center text-sm text-muted-foreground">
                No categories yet — add one below.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <CategoryColumn
                  label="Gutter"
                  categories={orderedGutter}
                  ids={gutterIds}
                  sensors={sensors}
                  onDragEnd={handleColumnDragEnd('gutter')}
                />
                <CategoryColumn
                  label="Insulation"
                  categories={orderedInsulation}
                  ids={insulationIds}
                  sensors={sensors}
                  onDragEnd={handleColumnDragEnd('insulation')}
                />
              </div>
            )}

            {creating ? (
              <NewCategoryRow onDone={() => setCreating(false)} />
            ) : (
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-secondary/20 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary/40 hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
                Add category
              </button>
            )}
          </div>
        )}
        </div>

        <DialogFooter>
          <Button variant="default" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// One product-line column: header + its own DndContext so reorder is scoped to
// this line and dragging never crosses into the other column.
function CategoryColumn({
  label,
  categories,
  ids,
  sensors,
  onDragEnd,
}: {
  label: string;
  categories: SkuCategory[];
  ids: string[];
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (e: DragEndEvent) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="eyebrow px-1 text-taupe">{label}</div>
      {categories.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-secondary/30 px-4 py-6 text-center text-xs text-muted-foreground">
          No categories
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {categories.map((cat) => (
                <CategoryRow key={cat.id} category={cat} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

function CategoryRow({ category }: { category: SkuCategory }) {
  const queryClient = useQueryClient();
  const skuCount = category.skuCount ?? 0;
  const canDelete = skuCount === 0;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [markup, setMarkup] = useState(String(category.defaultMarkupPct));

  // Sortable. Disabled while editing so the inline form is fully interactive.
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id: category.id,
    disabled: editing,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  useEffect(() => {
    if (!editing) {
      setName(category.name);
      setMarkup(String(category.defaultMarkupPct));
    }
  }, [editing, category.name, category.defaultMarkupPct]);

  const updateMutation = useMutation({
    mutationFn: (input: Partial<SkuCategoryCreateInput>) =>
      patchSkuCategory(category.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSkuCategory(category.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const markupNum = Number(markup);
    if (Number.isNaN(markupNum) || markupNum < 0 || markupNum > 1000) return;
    updateMutation.mutate({ name: trimmed, defaultMarkupPct: markupNum });
  };

  const handleDelete = () => {
    if (!canDelete) return;
    const ok = window.confirm(`Delete category "${category.name}"?`);
    if (!ok) return;
    deleteMutation.mutate();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-card px-2 py-2 transition-colors',
        isDragging ? 'border-primary' : 'border-border hover:bg-secondary/30',
      )}
    >
      <span
        {...(editing ? {} : { ...attributes, ...listeners })}
        className={cn(
          'flex h-7 w-5 shrink-0 items-center justify-center text-taupe',
          editing ? 'opacity-30' : 'cursor-grab active:cursor-grabbing',
        )}
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </span>

      {editing ? (
        <>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setEditing(false);
            }}
            disabled={updateMutation.isPending}
            className="h-8 flex-1"
            autoFocus
          />
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0"
              max="1000"
              step="0.01"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
              disabled={updateMutation.isPending}
              className="h-8 w-20 text-right"
            />
            <span className="text-xs text-taupe">%</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleSave}
            disabled={updateMutation.isPending || !name.trim()}
            className="h-7 w-7 p-0 hover:text-primary"
            title="Save"
          >
            <CheckIcon className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(false)}
            disabled={updateMutation.isPending}
            className="h-7 w-7 p-0"
            title="Cancel"
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <>
          <div className="flex-1 truncate text-sm font-semibold text-foreground">
            {category.name}
          </div>
          {/* Fixed-width columns so the markup text left-aligns across rows
              regardless of the SKU-badge width (a 3-digit pill no longer
              pushes the markup left). */}
          <span className="tnum w-24 shrink-0 text-left text-xs text-muted-foreground">
            {category.defaultMarkupPct}% markup
          </span>
          <div className="w-20 shrink-0 text-left text-xs">
            {skuCount > 0 ? (
              <Badge variant="secondary" className="tnum">
                {skuCount} SKU{skuCount === 1 ? '' : 's'}
              </Badge>
            ) : (
              <span className="text-taupe">empty</span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(true)}
            className="h-7 w-7 p-0"
            title="Rename / edit markup"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            disabled={!canDelete || deleteMutation.isPending}
            className={cn(
              'h-7 w-7 p-0',
              !canDelete && 'cursor-not-allowed opacity-30',
              canDelete && 'hover:text-destructive',
            )}
            title={
              !canDelete
                ? `Cannot delete — ${skuCount} SKU${skuCount === 1 ? '' : 's'} use this category`
                : 'Delete category'
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}

function NewCategoryRow({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [markup, setMarkup] = useState('0');

  const createMutation = useMutation({
    mutationFn: (input: SkuCategoryCreateInput) => postSkuCategory(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      onDone();
    },
  });

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const markupNum = Number(markup);
    if (Number.isNaN(markupNum) || markupNum < 0 || markupNum > 1000) return;
    createMutation.mutate({
      name: trimmed,
      defaultMarkupPct: markupNum,
      // New categories land at the end; reorder via drag.
      displayOrder: 999,
    });
  };

  return (
    <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/[0.04] px-2 py-2">
      <span className="flex h-7 w-5 shrink-0 items-center justify-center text-taupe opacity-30">
        <GripVertical className="h-4 w-4" />
      </span>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') onDone();
        }}
        placeholder="New category name"
        disabled={createMutation.isPending}
        autoFocus
        className="h-8 flex-1"
      />
      <div className="flex items-center gap-1">
        <Input
          type="number"
          min="0"
          max="1000"
          step="0.01"
          value={markup}
          onChange={(e) => setMarkup(e.target.value)}
          disabled={createMutation.isPending}
          className="h-8 w-20 text-right"
        />
        <span className="text-xs text-taupe">%</span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleSave}
        disabled={createMutation.isPending || !name.trim()}
        className="h-7 w-7 p-0 hover:text-primary"
        title="Create"
      >
        <CheckIcon className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onDone}
        disabled={createMutation.isPending}
        className="h-7 w-7 p-0"
        title="Cancel"
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
